/**
 * PATH: packages/ui/src/components/settings/security-tab/InAppAuthenticatorSection.tsx
 * PURPOSE: Built-in authenticator — register the 2FA setup key on this
 *          device, then show a live rotating 6-digit code with copy.
 * WHY: Client ask (2026-07): "Have MFA built into the apps as an option
 *      to generate tokens here.. and copy paste them once it is
 *      registered to a device". Purely optional; MFA enforcement stays
 *      off unless enabled in settings (org policy section).
 * FLOW:
 *   ┌────────────────┐   ┌───────────────┐   ┌────────────────┐
 *   │ stored secret? │ → │ live TOTP code│ → │ copy / forget  │
 *   │ else: register │   │ + countdown   │   │ this device    │
 *   └────────────────┘   └───────────────┘   └────────────────┘
 * DEPENDENCIES:
 *   - ./totp: RFC 6238 code generation + per-device secret storage
 */

import { useCallback, useEffect, useState } from 'react'
import { KeyRound, ClipboardCopy, Trash2 } from 'lucide-react'
import {
  generateTotpCode,
  getStoredTotpSecret,
  isValidTotpSecret,
  removeTotpSecret,
  storeTotpSecret,
} from './totp'

export interface InAppAuthenticatorSectionProps {
  /** Scope the stored secret per user so shared devices don't collide. */
  userId?: string
}

export function InAppAuthenticatorSection({ userId }: InAppAuthenticatorSectionProps) {
  const [secret, setSecret] = useState<string | null>(() => getStoredTotpSecret(userId))
  const [code, setCode] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [keyInput, setKeyInput] = useState('')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  // Tick the code every second while a secret is registered.
  useEffect(() => {
    if (!secret) return
    let cancelled = false
    const tick = async () => {
      try {
        const next = await generateTotpCode(secret)
        if (!cancelled) {
          setCode(next.code)
          setSecondsRemainingSafe(next.secondsRemaining)
        }
      } catch {
        if (!cancelled) setError('Stored key is invalid — remove it and register again.')
      }
    }
    const setSecondsRemainingSafe = (s: number) => setSecondsLeft(s)
    void tick()
    const id = window.setInterval(() => void tick(), 1000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [secret])

  const handleRegister = useCallback(() => {
    setError('')
    const cleaned = keyInput.toUpperCase().replace(/[\s=-]/g, '')
    if (!isValidTotpSecret(cleaned)) {
      setError('That does not look like a valid setup key (base32, 16+ characters).')
      return
    }
    storeTotpSecret(cleaned, userId)
    setSecret(cleaned)
    setKeyInput('')
  }, [keyInput, userId])

  const handleForget = useCallback(() => {
    removeTotpSecret(userId)
    setSecret(null)
    setCode('')
    setError('')
  }, [userId])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Copy failed — long-press the code to copy it manually.')
    }
  }, [code])

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-muted">
          <KeyRound className="w-5 h-5 text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">In-App Authenticator</h3>
          <p className="text-sm text-muted-foreground">
            Generate your 2FA codes right here instead of a separate authenticator app.
          </p>
        </div>
      </div>

      {secret ? (
        <div className="space-y-3">
          <div className="flex items-center gap-4 p-4 rounded-xl border border-border">
            <div className="font-mono text-3xl tracking-[0.3em] text-foreground" data-testid="totp-code">
              {code || '······'}
            </div>
            <div className="text-xs text-muted-foreground w-16">
              rotates in {secondsLeft}s
            </div>
            <button
              onClick={() => void handleCopy()}
              disabled={!code}
              className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
            >
              <ClipboardCopy className="w-4 h-4" /> {copied ? 'Copied' : 'Copy code'}
            </button>
          </div>
          <button
            onClick={handleForget}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium text-destructive"
          >
            <Trash2 className="w-4 h-4" /> Remove from this device
          </button>
          <p className="text-xs text-muted-foreground">
            The key is stored only on this device. Anyone with access to this
            device and your password could pass 2FA — keep the device locked.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Paste the <span className="font-medium">setup key</span> shown during
            two-factor setup (the same key behind the QR code) to register this
            device as an authenticator.
          </p>
          <div className="flex gap-2">
            <input
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="e.g. JBSWY3DPEHPK3PXP"
              autoComplete="off"
              spellCheck={false}
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono"
              aria-label="Setup key"
            />
            <button
              onClick={handleRegister}
              disabled={!keyInput.trim()}
              className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
            >
              Register this device
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-destructive mt-2">{error}</p>}
    </section>
  )
}

export default InAppAuthenticatorSection
